// ---------------------------------------------------------------------------
// OfflineBanner — top-of-screen pill that appears when the device drops
// connectivity, plus a one-time toast on transitions. Designed to read as
// information, not failure: clear status + what the app is still doing.
// ---------------------------------------------------------------------------

import { useEffect, useRef } from "react";
import { CloudOff, Cloud, Upload } from "lucide-react";
import { toast } from "sonner";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { parseOfflineTripFile, saveOfflineTrip, type OfflineTripSnapshot } from "@/utils/offlineCache";

interface Props {
  /** When true, the banner mentions that cached routes are being shown. */
  hasCachedTrip?: boolean;
  /** When provided, an "Import" affordance appears so a user can load a
   *  previously-exported trip JSON file (useful on a fresh device). */
  onImported?: (snap: OfflineTripSnapshot) => void;
}

export const OfflineBanner = ({ hasCachedTrip = false, onImported }: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const online = useOnlineStatus();
  const wasOnlineRef = useRef(online);

  // One-shot toasts on transitions — the persistent banner handles the
  // "still offline" state so we don't keep spamming.
  useEffect(() => {
    const wasOnline = wasOnlineRef.current;
    if (wasOnline && !online) {
      toast.error("You're offline", {
        description: hasCachedTrip
          ? "Showing your last saved route. New searches will resume when you reconnect."
          : "Route search needs an internet connection. Reconnect to plan a new trip.",
        duration: 6000,
      });
    } else if (!wasOnline && online) {
      toast.success("Back online", {
        description: "Routing is available again.",
        duration: 3500,
      });
    }
    wasOnlineRef.current = online;
  }, [online, hasCachedTrip]);

  if (online) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed left-1/2 -translate-x-1/2 z-[60] pointer-events-none"
      style={{ top: "max(calc(env(safe-area-inset-top) + 0.5rem), 3rem)" }}
    >
      <div className="pointer-events-auto inline-flex items-center gap-2 rounded-full border border-amber-300/70 bg-amber-50/95 dark:bg-amber-950/80 dark:border-amber-700/60 backdrop-blur px-3 py-1.5 text-xs font-medium text-amber-900 dark:text-amber-100 shadow-soft">
        <CloudOff className="w-3.5 h-3.5" aria-hidden="true" />
        <span>
          Offline
          {hasCachedTrip ? (
            <span className="text-amber-700/80 dark:text-amber-200/80 font-normal">
              {" "}· showing cached route
            </span>
          ) : (
            <span className="text-amber-700/80 dark:text-amber-200/80 font-normal">
              {" "}· new searches paused
            </span>
          )}
        </span>
        {!hasCachedTrip && onImported && (
          <>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="ml-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium border border-amber-300/70 dark:border-amber-700/60 hover:bg-amber-200/60 dark:hover:bg-amber-800/60"
              aria-label="Import a previously exported trip"
            >
              <Upload className="w-3 h-3" aria-hidden="true" />
              Import trip
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                try {
                  const snap = await parseOfflineTripFile(file);
                  saveOfflineTrip(snap);
                  onImported(snap);
                  toast.success("Trip imported", {
                    description: `Restored ${snap.scored.length} ${snap.scored.length === 1 ? "route" : "routes"} from file.`,
                    duration: 3500,
                  });
                } catch (err) {
                  toast.error("Couldn't import that file", {
                    description: err instanceof Error ? err.message : "Unknown error.",
                  });
                }
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};

/** Small inline indicator suitable for embedding inside other UI (e.g.
 *  the trip search bar) — complements the global banner. */
export const OfflinePill = () => {
  const online = useOnlineStatus();
  if (online) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 dark:text-emerald-300">
        <Cloud className="w-3 h-3" aria-hidden="true" />
        online
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-300">
      <CloudOff className="w-3 h-3" aria-hidden="true" />
      offline
    </span>
  );
};
